// Import utils
import { getDocumentElement, getDocumentElements, updateFieldTotal } from '../../Utils/Utils';

/**
 * Handle the tank volume calculation here.
 * 
 * Calculation shown below (in litres)
 * Volume of a tank (litres) = πr²x height x 1000
 * 
 * @param   {integer}       height 
 * @param   {integer}       radius 
 * @param   {HTMLElement}   volumeField 
 */
const TankVolumeCalcHandler = (height, radius, volumeField) => {
    let tankVolume = 0;

    if (height > 0 && radius > 0 && volumeField instanceof HTMLElement) {
        tankVolume = (Math.PI * Math.pow(radius, 2)) * height * 1000;
    }

    volumeField.value = parseInt(tankVolume);
};

/**
 * Callback function to execute when mutations are observed. This will
 * only fire the calculation if we notice a change in the childlist nodes.
 * 
 * @param       {array}     mutationList
 * 
 * @returns     {void}
 */
const TankVolumeCalcChildNodeObserver = (mutationList) => {
    mutationList.forEach(mutation => {
        if (mutation.type === "childList") {
            TankVolumeCalc(true);
        }
    });
};

/**
 * Set the childlist mutation observer.
 * 
 * @returns     {void}
 */
const SetTankVolumeCalcMutationObserver = () => {
    const Accordion = getDocumentElement('#tank-volume');
    const ObserverConfig = { childList: true, subtree: true };

    const Observer = new MutationObserver(TankVolumeCalcChildNodeObserver);
    Observer.observe(Accordion, ObserverConfig);
};

/**
 * Tank volume calculation.
 * 
 * @param   {boolean}       mounted
 * 
 * @returns     {void}
 */
export const TankVolumeCalc = (mounted) => {
    const TankHeightFields = getDocumentElements('#tank-volume [id^="tankHeight-"]');
    const TankRadiusFields = getDocumentElements('#tank-volume [id^="tankRadius-"]');
    const TankVolumeFields = getDocumentElements('#tank-volume [id^="tankSubtotal-"]');
    const TankLitresVolumeTotal = getDocumentElement('#tank-volume #tankLitresTotal');
    const TankMegaLitresVolumeTotal = getDocumentElement('#tank-volume #tankMegaTotal');
    const TankMegaLitresVolumeSummaryTotal = getDocumentElement('#overall-farm-water-storage #tot_tanks_volume');

    if (TankHeightFields && TankRadiusFields && TankVolumeFields) {
        if (mounted === false) {
            SetTankVolumeCalcMutationObserver();
        }

        const FieldsToMount = [TankHeightFields, TankRadiusFields];
        let megaL = 0;

        FieldsToMount.forEach(fieldSelection => {
            fieldSelection.forEach((field, i) => {
                field.addEventListener('input', e => {
                    e.stopImmediatePropagation();

                    TankVolumeCalcHandler(
                        parseFloat(TankHeightFields[i].value),
                        parseFloat(TankRadiusFields[i].value),
                        TankVolumeFields[i]
                    );

                    updateFieldTotal(TankVolumeFields, TankLitresVolumeTotal);

                    // Convert litres to megalitres
                    megaL = Number(TankLitresVolumeTotal.value) / 1000000;
                    TankMegaLitresVolumeTotal.value = megaL.toFixed(3);
                    TankMegaLitresVolumeSummaryTotal.value = megaL.toFixed(3);
                });
            });
        });

        // listen for clone removed and re-update totals
        document.addEventListener('clonable:removed', e => {
            if (e.detail.id === 'tank') {
                updateFieldTotal(TankVolumeFields, TankLitresVolumeTotal);
                megaL = Number(TankLitresVolumeTotal.value) / 1000000;
                TankMegaLitresVolumeTotal.value = megaL.toFixed(3);
                TankMegaLitresVolumeSummaryTotal.value = megaL.toFixed(3);
            }
        });
    }
};
